// --------------------------------------------------------
// UNIT REGISTER - UnitRegister
// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
import { Formik } from "formik";
import React, { useCallback, useState } from "react";

import RegisterUnitForm from "../../components/form/RegisterUnitForm";
import ContainerWithSidebar from "../../components/UI/containerWithSidebar/ContainerWithSidebar";
import * as yup from "yup";
import {
  fillUnitObjectForEdit,
  handleBackendErrors,
  isEmptyObject,
  validateCNPJ,
  validateEstado,
  validateOnlyLetters,
  validateSchema,
  validateTelephone,
} from "../../helpers/HelperFunctions";
import { Paths } from "../../enums/Paths";
import { useLocation, useNavigate } from "react-router-dom";
import SuccessModal from "../../components/successModal/SuccessModal";
import { ReactComponent as MedicineCheckIcon } from "../../assets/imgs/medicineCheck.svg";
import { initialObjectUnitForm } from "../../constants/Objects";
import { useAppDispatch } from "../..";
import * as unitActions from "../../store/actions/unit";
import GenericModal from "../../components/genericModal/GenericModal";
import { GenericSolidButton } from "../../components/UI/buttons/Button.styled";
import Colors from "../../constants/Colors";
import useResponsiveInfo from "../../hooks/useResponsiveInfo";
import { GeneralContainer } from "../../components/UI/generalLayout/Layout.styled";

/**
 * A component that renders a Screen.
 *
 * @param props Properties
 * @param props.route The route
 * @param props.navigation The navigator
 * @returns The component JSX.
 */
function UnitRegister(): JSX.Element {
  // --------------------------------------------------------
  // UNIT REGISTER PRIVATE DECLARATIONS
  // --------------------------------------------------------
  const { state }: any = useLocation();

  const getInitialValues = useCallback(() => {
    if (state != null) {
      const { unit }: any = state;
      return fillUnitObjectForEdit(unit);
    } else {
      return initialObjectUnitForm;
    }
  }, [state]);

  const [isConfirmationState, setIsConfirmationState] = useState(false);
  const [showModalCancellation, setShowModalCancellation] = useState(false);
  const [showModalConfirmation, setShowModalConfirmation] = useState(false);
  const [isEdit] = useState(state != null);
  const [isView] = useState(state != null && state.isView);
  const { isMobile } = useResponsiveInfo();
  const [err, setErr] = useState<any[]>([]);

  const [initialValues] = useState(getInitialValues());
  const dispatch = useAppDispatch();

  const validationSchema = yup.object().shape({
    nomeFantasia: yup
      .string()
      .max(150)
      .min(3, "Digite pelo menos 3 caracteres")
      .required("Nome da Unidade de atendimento é um campo obrigatório")
      .test("validar-nome", "Digite apenas letras", value => {
        return validateOnlyLetters(value ?? "") === true;
      }),
    cnpj: yup
      .string()
      .required("CNPJ é um campo obrigatório")
      .test("validar-cnpj", "O CNPJ é inválido", value => {
        return validateCNPJ(value ?? "") === true;
      }),
    cep: yup
      .string()
      .matches(new RegExp(/\d{2}[.]?\d{3}[-]?\d{3}/), "O CEP precisa ter 8 caracteres")
      .required("CEP é um campo obrigatório"),
    logradouro: yup
      .string()
      .max(200)
      .required("Endereço é um campo obrigatório"),
    numero: yup
      .string()
      .matches(new RegExp(/^[0-9]*$/, "g"), "Digite apenas números")
      .optional()
      .nullable(),
    complemento: yup.string().max(50).nullable(),
    cidade: yup.string().max(200).required("Cidade é um campo obrigatório"),
    bairro: yup.string().max(200).required("Bairro é um campo obrigatório"),
    estado: yup
      .string()
      .max(2)
      .required("Estado é um campo obrigatório")
      .test("validar-estado", "Sigla de Estado inexistente", value => {
        return validateEstado(value ?? "") === true;
      }), //Apenas siglas (pode ser feito também com test)
    telefone1: yup
      .string()
      .required("Telefone da unidade é um campo obrigatório")
      .test("validar-telefone", "Telefone inválido", value => {
        return validateTelephone(value ?? "") === true;
      }), //usar test para validação de ddd
    telefone2: yup
      .string()
      .optional()
      .test("validar-telefone", "Telefone inválido", value => {
        return value !== undefined && value !== ""
          ? validateTelephone(value ?? "") === true
          : true;
      }),
  });

  const navigate = useNavigate();

  const navigateToUnitList = () => {
    return navigate(Paths.UnitList);
  };

  const registerUnit = async (values: any) => {
    try {
      await dispatch(unitActions.registerUnit(values));
      setShowModalConfirmation(true);
    } catch (error: any) {
      setShowModalConfirmation(false);
      handleBackendErrors(error, setErr);
      //Montar um esquema de mensagens de erro aqui
      setIsConfirmationState(false);
    }
  };

  const updateUnit = async (values: any, unitId: string) => {
    try {
      await dispatch(unitActions.updateUnit(values, unitId));
      setShowModalConfirmation(true);
    } catch (error: any) {
      setShowModalConfirmation(false);
      handleBackendErrors(error, setErr);
      //Montar um esquema de mensagens de erro aqui
      setIsConfirmationState(false);
    }
  };

  const onConfirmClick = async (values: any) => {
    isConfirmationState
      ? isEdit
        ? await updateUnit(values, initialValues.id)
        : await registerUnit(values)
      : await validateSchema(values, validationSchema, () => {
          setIsConfirmationState(true);
        });
  };

  const onCancelClick = (values: any) => {
    if (!isEmptyObject(values)) {
      setShowModalCancellation(true);
    } else {
      navigateToUnitList();
    }
  };

  return (
    <ContainerWithSidebar>
      <GeneralContainer>
        <Formik
          initialValues={initialValues}
          onSubmit={() => {}}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {formik => {
            const { values, errors } = formik;

            return (
              <RegisterUnitForm
                error={err}
                onCloseError={() => {
                  setErr([]);
                }}
                initialValues={initialValues}
                isConfirmationState={isConfirmationState}
                isEditState={isEdit}
                isViewState={isView}
                formValues={values}
                formErrors={errors}
                onClickCancel={
                  isConfirmationState
                    ? () => {
                        setIsConfirmationState(prev => !prev);
                      }
                    : () => {
                        onCancelClick(values);
                      }
                }
                onClickConfirmar={() => {
                  onConfirmClick(values);
                }}
              />
            );
          }}
        </Formik>

        {isView && (
          <>
            <GenericSolidButton
              onClick={navigateToUnitList}
              color={Colors.SPM600}
              style={{
                width: isMobile ? "100%" : "10%",
                marginBottom: isMobile ? 24 : 2,
              }}
            >
              Voltar
            </GenericSolidButton>
          </>
        )}

        <GenericModal
          modalTitle={"Tem certeza que deseja sair do cadastro de unidade?"}
          modalSubtitle={"Os dados preenchidos não serão salvos"}
          cancelButtonMsg={"Confirmar"}
          confirmButtonMsg={"Voltar"}
          visible={showModalCancellation}
          onCancel={() => {
            setShowModalCancellation(false);
          }}
          onConfirm={navigateToUnitList}
        />

        <SuccessModal
          Icon={() => {
            return <MedicineCheckIcon />;
          }}
          modalMsg="Cadastro concluído com sucesso!"
          visible={showModalConfirmation}
          onVoltar={navigateToUnitList}
        />
      </GeneralContainer>
    </ContainerWithSidebar>
  );
}

export default UnitRegister;
